import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const OPEN_TO = ["Hiring", "Looking for work", "Mentoring", "Cofounder", "Speaking", "Advising"];
const field =
  "w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900";

export const OnboardingModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ headline: "", working_on: "", company: "", skills: "", open_to: [] });

  useEffect(() => {
    if (!user) return;
    if (window.location.pathname === "/") return;
    if (sessionStorage.getItem("onboarding_dismissed")) return;
    apiFetch("/me/profile")
      .then((p) => {
        if (!p.headline && !p.working_on && !p.skills?.length) setOpen(true);
      })
      .catch(() => {});
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem("onboarding_dismissed", "1");
    setOpen(false);
  };

  if (!open) return null;

  const finish = async () => {
    try {
      await apiFetch("/me/profile", {
        method: "PUT",
        body: JSON.stringify({
          headline: form.headline,
          working_on: form.working_on,
          company: form.company,
          skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
          open_to: form.open_to,
          neighborhood: "",
          site_url: "",
          linkedin_url: "",
          github_url: "",
          notify_replies: true,
          notify_dms: true,
        }),
      });
      toast.success("Welcome in — your profile is live");
      setOpen(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6">
      <div className="w-full max-w-lg rounded-t-2xl bg-white p-7 sm:rounded-2xl" data-testid="onboarding-modal">
        <div className="flex items-start justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-neutral-700">
              <Sparkles className="h-3 w-3 text-[#4D7C0F]" /> Step {step} of 2
            </span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-neutral-900">
              {step === 1 ? `Welcome, ${user?.name?.split(" ")[0] || "friend"}` : "What are you open to?"}
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              {step === 1
                ? "A two-line profile helps other members know who they're talking to."
                : "Members can filter the directory by these."}
            </p>
          </div>
          <button
            onClick={dismiss}
            data-testid="onboarding-skip"
            className="rounded-full p-2 transition-colors duration-200 hover:bg-neutral-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 1 ? (
          <div className="mt-6 space-y-3">
            <input
              data-testid="onboarding-headline"
              className={field}
              placeholder="Headline — e.g. Platform engineer at Jama"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
            />
            <textarea
              data-testid="onboarding-working"
              rows={2}
              className={field}
              placeholder="What are you working on right now?"
              value={form.working_on}
              onChange={(e) => setForm({ ...form, working_on: e.target.value })}
            />
            <input
              data-testid="onboarding-company"
              className={field}
              placeholder="Company (optional)"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
            <input
              data-testid="onboarding-skills"
              className={field}
              placeholder="Skills, comma separated"
              value={form.skills}
              onChange={(e) => setForm({ ...form, skills: e.target.value })}
            />
          </div>
        ) : (
          <div className="mt-6 flex flex-wrap gap-2">
            {OPEN_TO.map((o) => {
              const on = form.open_to.includes(o);
              return (
                <button
                  key={o}
                  data-testid={`onboarding-open-${o}`}
                  onClick={() =>
                    setForm({
                      ...form,
                      open_to: on ? form.open_to.filter((x) => x !== o) : [...form.open_to, o],
                    })
                  }
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    on ? "border-[#84CC16] bg-[#B8FF2E] text-[#14210A]" : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  {o}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-7 flex items-center justify-between gap-3">
          <button
            onClick={dismiss}
            data-testid="onboarding-later"
            className="text-sm font-semibold text-neutral-500 transition-colors duration-200 hover:text-neutral-900"
          >
            Maybe later
          </button>
          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              data-testid="onboarding-next"
              className="rounded-full bg-neutral-900 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-neutral-700"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={finish}
              data-testid="onboarding-finish"
              className="rounded-full bg-[#B8FF2E] px-6 py-3 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
            >
              Save profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
